import { useContext, useMemo } from "react";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import {
  InfoCard,
  InfoContent,
  InfoText
} from "../../../../components/atoms/InfoCard";
import { DateRangeContext } from "../context/DateRangeContext";
import { RefreshContext } from "../context/RefreshContext";
import { useZoom } from "../context/ZoomContext";
import { useGetHorometroRangoFechas } from "../hooks/useGetHorometroRangoFechas";
import { getJumboInfo } from "../lib/utils";
import { HorometroChartErrorBoundary } from "./BoundaryErrors/HorometroChart";
import { SkeletonBarChart } from "./SkeletonBarChart";

function formatDateLocal(date) {
  if (date.includes(" ")) {
    const [datePart, timePart] = date.split(" ");
    const [year, month, day] = datePart.split("-");
    const [hours, minutes, seconds] = timePart.split(":");

    return new Date(
      Number.parseInt(year),
      Number.parseInt(month) - 1,
      Number.parseInt(day),
      Number.parseInt(hours),
      Number.parseInt(minutes),
      Number.parseInt(seconds)
    );
  }

  const now = new Date();
  const [year, month, day] = date.split("-");
  return new Date(
    Number.parseInt(year),
    Number.parseInt(month) - 1,
    Number.parseInt(day),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds()
  );
}

const tipoColors = {
  "Motor Diesel": "#9cd5ba",
  "Motor": "#01b7c4",     
  "Percutor": "#01b7c4",  
  "mantencion": "#ffd971"
};

function getColorForTipo(tipo) {
  if (tipoColors[tipo]) return tipoColors[tipo];

  const match = tipo.match(/^(Motor|Percutor) \d+$/);
  
  if (match) {
    const baseTipo = match[1];

    return tipoColors[baseTipo] || "#01b7c4";
  }
  
  return "#01b7c4";
}

function getOrdenTipoBase(horometro) {
  const tiposBaseSet = new Set();
  let maxMotorN = 0;
  let maxPercutorN = 0;
  let hasDieselEngine = false;
  let hasMaintenance = false;

  horometro.forEach((item) => {
    let tipo = item.tipo_movimiento;
    if (tipo.startsWith("fuera de servicio ")) {
      tipo = tipo.replace("fuera de servicio ", "");
    }
    tiposBaseSet.add(tipo);
    if (/^Motor (\d+)$/.test(tipo)) {
      const n = Number.parseInt(tipo.split(" ")[1], 10);
      maxMotorN = Math.max(maxMotorN, n);
    }
    if (/^Percutor (\d+)$/.test(tipo)) {
      const n = Number.parseInt(tipo.split(" ")[1], 10);
      maxPercutorN = Math.max(maxPercutorN, n);
    }
    if (tipo === "Motor Diesel") hasDieselEngine = true;
    if (tipo === "mantencion") hasMaintenance = true;
  });

  const ordenTipoBase = [];
  if (hasDieselEngine) ordenTipoBase.push("Motor Diesel");
  const maxN = Math.max(maxMotorN, maxPercutorN);
  for (let i = 1; i <= maxN; i++) {
    ordenTipoBase.push(`Motor ${i}`);
    ordenTipoBase.push(`Percutor ${i}`);
  }
  if (hasMaintenance) ordenTipoBase.push("mantencion");
  return ordenTipoBase;
}

const getTipoLabel = (t, tipoBase) => {
  const match = tipoBase.match(/^(Motor|Percutor) (\d+)$/);
  if (match) {
    const [_, tipo, n] = match;
    return t(`${tipo}_n`, { n });
  }

  return t(tipoBase);
};

export function JumboHorometroChart() {
  const { idMaquina } = getJumboInfo();
  const { finalDate } = useContext(DateRangeContext);
  const { refreshKey } = useContext(RefreshContext);
  const { startDate, endDate } = finalDate;
  const { zoomRange, setZoomRange } = useZoom();
  const { t, i18n } = useTranslation();

  const { horometro, loadingHorometro, errorHorometro } =
    useGetHorometroRangoFechas(idMaquina, startDate, endDate, refreshKey);

  const ordenTipoBase = getOrdenTipoBase(horometro);
  const ordenTipo = ordenTipoBase.map((tipo) => getTipoLabel(t, tipo));

  const processedData = horometro.map((item) => {
    const isFueraServicio =
      item.tipo_movimiento.startsWith("fuera de servicio");
    const tipoBase = isFueraServicio
      ? item.tipo_movimiento.replace("fuera de servicio ", "")
      : item.tipo_movimiento;
    const fechaInicio = formatDateLocal(item.fecha_inicio).getTime();
    const fechaFinal = formatDateLocal(item.fecha_fin).getTime();
    return {
      move: tipoBase,
      fueraDeServicio: isFueraServicio,
      data: [
        {
          x: getTipoLabel(t, tipoBase) || tipoBase,
          y: [fechaInicio, fechaFinal]
        }
      ]
    };
  });

  const sortedData = processedData.sort((a, b) => {
    const orderA = ordenTipo.indexOf(a.data[0].x);
    const orderB = ordenTipo.indexOf(b.data[0].x);
    return (orderA === -1 ? 99 : orderA) - (orderB === -1 ? 99 : orderB);
  });

  const getStartLimit = (date) => {
    const [year, month, day] = date.split("-");
    return new Date(
      Number.parseInt(year),
      Number.parseInt(month) - 1,
      Number.parseInt(day),
      7,
      0,
      0,
      0
    ).getTime();
  };

  const getEndLimit = (date) => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];

    if (date === todayStr) {
      return today.getTime();
    }

    const [year, month, day] = date.split("-");
    return new Date(
      Number.parseInt(year),
      Number.parseInt(month) - 1,
      Number.parseInt(day) + 1,
      7,
      0,
      0,
      0
    ).getTime();
  };

  const startLimit = getStartLimit(startDate);
  const endLimit = getEndLimit(endDate);

  const adjustDataToRange = (data, min, max) => {
    return data.map((item) => ({
      ...item,
      data: item.data.map((d) => ({
        ...d,
        y: [Math.max(d.y[0], min), Math.min(d.y[1], max)]
      }))
    }));
  };

  const options = useMemo(() => {
    const effectiveMin = zoomRange ? zoomRange[0] : startLimit;
    const effectiveMax = zoomRange ? zoomRange[1] : endLimit;

    return {
      chart: {
        animations: {
          enabled: false
        },
        events: {
          zoomed: (_, { xaxis }) => {
            if (xaxis.min && xaxis.max) {
              setZoomRange([xaxis.min, xaxis.max]);
            }
          },
          beforeZoom: (chartContext, { xaxis }) => {
            if (!xaxis.min || !xaxis.max) return false;
            return true;
          }
        },
        type: "rangeBar",
        zoom: {
          enabled: true,
          allowMouseWheelZoom: false,
          zoomedArea: {
            fill: {
              color: "#90CAF9",
              opacity: 0.1
            },
            stroke: {
              color: "#0D47A1",
              opacity: 0.1,
              width: 1
            }
          }
        },
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          rangeBarGroupRows: true,
          barHeight: "85%"
        }
      },
      yaxis: {
        axisBorder: {
          show: false
        },
        labels: {
          style: {
            fontSize: "11.5px",
            fontFamily: "inherit",
            fontWeight: 400,
            width: 120
          },
          minWidth: 111,
          margin: 10
        }
      },
      xaxis: {
        tickAmount: 12,
        axisBorder: {
          show: false
        },
        type: "datetime",
        dateTimeUTC: true,
        min: effectiveMin,
        max: effectiveMax,
        labels: {
          formatter: (val) => {
            if (!val) return "";

            const date = new Date(val);

            const timeToMax = Math.abs(val - effectiveMax);
            if (timeToMax < 1000 * 60 * 30) return "";

            return date
              .toLocaleString(i18n.language === "es" ? "es-CL" : "en-US", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false
              })
              .replace(":", ":");
          },
          hideOverlappingLabels: true,
          showDuplicates: false,
          style: {
            fontSize: "12px"
          },
          trim: true,
          tickPlacement: "on"
        }
      },
      tooltip: {
        enabled: true,
        x: {
          show: true,
          formatter: (val) => {
            if (typeof val === "number") {
              const date = new Date(val);
              return date.toLocaleString(
                i18n.language === "es" ? "es-CL" : "en-US",
                {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: false
                }
              );
            }
            return val;
          }
        }
      },
      legend: {
        show: false
      },
      colors: [
        ({ seriesIndex, w }) => {
          const fueraDeServicio = w.config.series[seriesIndex].fueraDeServicio;

          if (fueraDeServicio) return "#c5283d";

          const move = w.config.series[seriesIndex].move;
          
          return getColorForTipo(move);
        }
      ],
      fill: {
        type: "solid",
        opacity: 0.7
      }
    };
  }, [zoomRange, startLimit, endLimit, i18n.language, setZoomRange]);

  return (
    <section>
      {loadingHorometro && <SkeletonBarChart />}
      {!loadingHorometro && !errorHorometro && processedData?.length > 0 && (
        <section
          style={{
            position: "relative"
          }}>
          <Chart
            series={adjustDataToRange(
              sortedData,
              zoomRange ? zoomRange[0] : startLimit,
              zoomRange ? zoomRange[1] : endLimit
            )}
            options={options}
            type="rangeBar"
            height="300px"
            width="100%"
          />
        </section>
      )}
      {errorHorometro !== null && (
        <HorometroChartErrorBoundary>
          <InfoCard>
            <InfoContent
              style={{
                background: "#fff",
                padding: "1.5rem 1.85rem",
                border: "0.5px solid #e9e8f4",
                borderRadius: "5px",
                boxShadow: "0px 0px 3px rgba(0, 0, 0, 0.1)"
              }}>
              <InfoText color="primary">{t(errorHorometro)}</InfoText>
            </InfoContent>
          </InfoCard>
        </HorometroChartErrorBoundary>
      )}
    </section>
  );
}
